import './collapsible.scss';

import Icon, { IconType } from '../icon/Icon';
import Link, { LinkType, LinkVariant } from '../link/Link';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Tooltip, { TooltipProps } from '../tooltip/tooltip';

import { GetUniqueId } from '../../utils/id-utils';
import { getMatchMedia } from '../../utils/media-utils';
import { stringIsNullOrEmpty } from '../../utils/string-utils';

export enum CollapseSize {
  SMALL = 'micro',
  MEDIUM = 'medium',
  LARGE = 'large',
  TINY = 'tiny',
}
export enum CollapseTitleSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  default = 'default',
}

export type CollapseProps = {
  id?: number;
  open?: boolean;
  closeOn?: string;
  moreLabel?: string;
  lessLabel?: string;
  size?: CollapseSize;
  numberOfLines?: number;
  renderPartialSkeleton?: boolean;
  title?: string;
  titleSize?: CollapseTitleSize;
  tooltip?: TooltipProps;
  children?: React.ReactNode;
  shouldApplyTruncation?: boolean;
  onClickTooltip?: () => void;
};

const Title: React.FC<{ size?: string; children: React.ReactNode }> = ({
  children,
}) => {
  // if (!children) return null;
  // if (size === 'large') return <h3>{children}</h3>;
  // if (size === 'medium') return <h5">{children}</h5>;
  // if (size === 'small') return <h6">{children}</h6>;
  return <>{children}</>;
};

export const Collapse: React.FC<CollapseProps> = ({
  open = false,
  closeOn,
  moreLabel = 'More',
  lessLabel = 'Less',
  size = CollapseSize.TINY,
  numberOfLines = 3,
  renderPartialSkeleton = false,
  title,
  titleSize,
  tooltip,
  children,
  shouldApplyTruncation = false,
  onClickTooltip,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [eventTrigger, setEventTrigger] = useState(false);
  const [internalOpen, setInternalOpen] = useState(open);
  const id = useRef(GetUniqueId()).current;

  const iconSize = useMemo(() => {
    return (
      {
        medium: ' u-icon-small',
        large: ' u-icon-normal',
        micro: ' u-icon-micro',
        tiny: ' u-icon-tiny',
      }[size] || ' u-icon-tiny'
    );
  }, [size]);

  const handleResize = useCallback(() => {
    if (panelRef.current && (numberOfLines ?? 0) > 0) {
      const isOverflowing =
        panelRef.current.scrollHeight > panelRef.current.clientHeight;
      setHasMore(isOverflowing);
    }

    if (closeOn && !stringIsNullOrEmpty(closeOn)) {
      setInternalOpen(getMatchMedia(closeOn));
    }
  }, [closeOn, numberOfLines]);

  useEffect(() => {
    const observer = new ResizeObserver(handleResize);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [handleResize]);

  const toggleReadMore = () => {
    if ((numberOfLines ?? 0) <= 0) return;
    setExpanded((prev) => !prev);
    setEventTrigger(true);
    panelRef.current?.focus();
  };

  const shouldTruncate =
    shouldApplyTruncation &&
    numberOfLines !== undefined &&
    numberOfLines > 0 &&
    !expanded;

  const showMoreButton = (numberOfLines ?? 0) > 0 && (hasMore || eventTrigger);

  return (
    <details
      className="collapse"
      open={open}
      ref={detailsRef}
      data-size={size}
      onToggle={() => setInternalOpen(detailsRef.current?.open || false)}
    >
      <summary className="collapse__summary u-label-small">
        <div className="collapse__summary-title noselect">
          <Title size={titleSize}>
            {renderPartialSkeleton ? (
              <span
                className="o-skel__item noselect"
                style={{
                  width: '80%',
                  display: 'inline-block',
                  height: '1em',
                }}
              >
                &nbsp;
              </span>
            ) : (
              title
            )}
          </Title>

          {!renderPartialSkeleton && tooltip && (
            <Tooltip
              onClick={(e) => {
                e.stopPropagation();
                onClickTooltip?.();
              }}
              {...tooltip}
            />
          )}
        </div>

        <Icon
          iconType={IconType.ArrowDown}
          className={`collapse__icon ${iconSize} a-link__icon ${
            internalOpen ? 'collapse__icon--rotated' : ''
          }`}
        />
      </summary>

      {renderPartialSkeleton ? (
        <ul className="o-flex o-flex--column u-gap-2 u-mbs-2">
          {Array.from({ length: numberOfLines ?? 3 }).map((_, i) => (
            <li key={i} className="o-skel__item" style={{ marginLeft: '1rem' }}>
              &nbsp;
            </li>
          ))}
        </ul>
      ) : (
        <div className="collapse__panel">
          <div
            ref={panelRef}
            tabIndex={-1}
            id={`collapse${id}`}
            className={
              shouldTruncate ? 'u-truncate-multi-checkboxes' : undefined
            }
            style={
              shouldTruncate
                ? { ['--truncate-multi' as any]: numberOfLines }
                : undefined
            }
          >
            {children}
          </div>

          {showMoreButton && (
            <Link
              onClick={toggleReadMore}
              type={LinkType.PRIMARY}
              isSmall={true}
              variant={LinkVariant.BUTTON}
              icon={{ iconType: IconType.ArrowDown, className: iconSize }}
              ariaExpanded={expanded}
              ariaControls={`collapse${id}`}
              label={expanded ? lessLabel : moreLabel}
              className="collapse__more"
            />
          )}
        </div>
      )}
    </details>
  );
};
